import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { useUpgradePriceAnchor } from '@/hooks/use-upgrade-price-anchor';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Take the user to the plans page. */
  onUpgrade: () => void;
  /** Already-translated copy — callers own their i18n keys. */
  title: string;
  body: string;
  dismissLabel: string;
  upgradeLabel: string;
  /** Prefixes the data-testids: `<prefix>-dialog`, `<prefix>-dismiss`, `<prefix>-upgrade`. */
  testIdPrefix: string;
}

/** Click-time PRO gate dialog: the gated button stays clickable, and clicking
 *  is when the explanation and the upgrade CTA land. Shared by the layers
 *  export gate and the bulk-export gate so the pattern cannot drift. */
export const ProFeatureDialog: React.FC<Props> = ({
  open, onOpenChange, onUpgrade, title, body, dismissLabel, upgradeLabel, testIdPrefix,
}) => {
  // Price anchor: the user is mid-intent; "upgrade" with no number is one
  // unknown too many. Null (plans still loading / none paid) renders nothing.
  const priceAnchor = useUpgradePriceAnchor();
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid={`${testIdPrefix}-dialog`} className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-primary" />
          {title}
        </DialogTitle>
        <DialogDescription>{body}</DialogDescription>
        {priceAnchor && (
          <p
            data-testid={`${testIdPrefix}-price-anchor`}
            className="text-sm font-semibold text-primary"
          >
            {priceAnchor}
          </p>
        )}
      </DialogHeader>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          data-testid={`${testIdPrefix}-dismiss`}
          variant="secondary"
          onClick={() => onOpenChange(false)}
        >
          {dismissLabel}
        </Button>
        <Button data-testid={`${testIdPrefix}-upgrade`} variant="gradient" onClick={onUpgrade}>
          <Crown className="mr-1 h-3.5 w-3.5" />
          {upgradeLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default ProFeatureDialog;
